/**
 * Partners (REQ-018, the partner half).
 *
 * The multipliers of MAR-001 §6: insurers, tour operators, medical societies,
 * creators, clinics. What this replaces is three admin pages built from
 * hard-coded arrays — figures that looked like a business and described
 * nothing.
 *
 * Two things on this page are computed and shown as such, because the
 * requirement is explicit that neither may be typed: the lifecycle stage, and
 * the tier a partner has actually earned. Where the agreed tier is ahead of
 * the earned one the page says so plainly — that is a conversation somebody
 * owes the partner, not a demotion the system performs.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Building2,
  CalendarClock,
  Loader2,
  Plus,
  Search,
} from "lucide-react";
import { useState } from "react";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import apiClient from "@/services/api/apiClient";

type PartnerType =
  | "insurer"
  | "tour_operator"
  | "medical_society"
  | "creator"
  | "clinic";

type Stage =
  | "prospect"
  | "in_negotiation"
  | "contracted"
  | "active"
  | "dormant"
  | "ended";

interface Partner {
  id: string;
  organisation: string;
  type: PartnerType;
  typeLabel: string;
  earns: string;
  country: string | null;
  contactName: string | null;
  contactEmail: string | null;
  referralCodes: string[];
  stage: Stage;
  agreedTier: string;
  earnedTier: string;
  tierShortfall: { below: boolean; needs: number };
  attributedCustomers: number;
  payingCustomers: number;
  lastAttributionAt: string | null;
  contract: {
    startsOn: string | null;
    endsOn: string | null;
    noticeDays: number | null;
    commissionBps: number | null;
    notice: { inWindow: boolean; daysLeft: number | null };
  };
}

const STAGE_STYLE: Record<Stage, string> = {
  prospect: "bg-gray-50 text-gray-600 ring-gray-500/20",
  in_negotiation: "bg-sky-50 text-sky-700 ring-sky-600/20",
  contracted: "bg-violet-50 text-violet-700 ring-violet-600/20",
  active: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  dormant: "bg-amber-50 text-amber-700 ring-amber-600/20",
  ended: "bg-gray-100 text-gray-500 ring-gray-400/20",
};

const STAGE_LABEL: Record<Stage, string> = {
  prospect: "Prospect",
  in_negotiation: "In negotiation",
  contracted: "Contracted",
  active: "Active",
  dormant: "Dormant",
  ended: "Ended",
};

export default function PartnersPage() {
  const fmt = useLocaleFormat();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const partners = useQuery({
    queryKey: ["partners", search],
    queryFn: async () => {
      const query =
        search.trim().length > 1
          ? `?q=${encodeURIComponent(search.trim())}`
          : "";
      const response = await apiClient.get<{ data: { partners: Partner[] } }>(
        `/staff/partners${query}`,
      );
      return response.data.data.partners;
    },
  });

  const types = useQuery({
    queryKey: ["partners", "types"],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { types: { id: string; label: string; earns: string }[] };
      }>("/staff/partners/types");
      return response.data.data.types;
    },
  });

  const add = useMutation({
    mutationFn: async (form: FormData) => {
      const codes = String(form.get("codes") ?? "")
        .split(/[,\s]+/)
        .map((code) => code.trim())
        .filter(Boolean);
      await apiClient.post("/staff/partners", {
        organisation: String(form.get("organisation") ?? "").trim(),
        type: String(form.get("type")),
        country: String(form.get("country") ?? "").trim() || undefined,
        contactName: String(form.get("contactName") ?? "").trim() || undefined,
        contactEmail:
          String(form.get("contactEmail") ?? "").trim() || undefined,
        referralCodes: codes,
        contract: {
          startsOn: String(form.get("startsOn") ?? "") || undefined,
          endsOn: String(form.get("endsOn") ?? "") || undefined,
          noticeDays: Number(form.get("noticeDays")) || undefined,
        },
      });
    },
    onSuccess: () => {
      setRefusal(null);
      setAdding(false);
      void queryClient.invalidateQueries({ queryKey: ["partners"] });
    },
    onError: (error: unknown) => {
      // The server refuses for stated reasons — a referral code already
      // belonging to somebody else, for one. Its words are more use than
      // "failed", because they say what to change.
      const said = (
        error as { response?: { data?: { error?: { message?: string } } } }
      )?.response?.data?.error?.message;
      setRefusal(said ?? "The partner could not be created.");
    },
  });

  const noticeSoon = partners.data?.filter((p) => p.contract.notice.inWindow);

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Building2 className="h-5 w-5 text-sky-600" /> Partners
        </h1>
        <div className="flex items-center gap-2">
          <label className="relative w-64">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name, contact or code"
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-sky-500 focus:outline-none"
            />
          </label>
          <button
            onClick={() => setAdding((open) => !open)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700"
          >
            <Plus className="h-4 w-4" /> Add a partner
          </button>
        </div>
      </div>

      {/* Contracts that need a decision. Above the table, because a notice
          period discovered on the day it starts has already been missed. */}
      {noticeSoon && noticeSoon.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">
              {noticeSoon.length === 1
                ? "One contract needs a decision"
                : `${noticeSoon.length} contracts need a decision`}
            </p>
            <p className="mt-1">
              {noticeSoon
                .map(
                  (p) =>
                    `${p.organisation} — ${p.contract.notice.daysLeft} days to give notice`,
                )
                .join("; ")}
              .
            </p>
          </div>
        </div>
      )}

      {adding && (
        <form
          className="grid gap-3 rounded-lg border border-gray-200 bg-white p-4 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            add.mutate(new FormData(event.currentTarget));
          }}
        >
          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Organisation
            </span>
            <input
              name="organisation"
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700">Type</span>
            <select
              name="type"
              defaultValue="creator"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              {types.data?.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.label} — {type.earns}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Contact name
            </span>
            <input
              name="contactName"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Contact email
            </span>
            <input
              name="contactEmail"
              type="email"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>

          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block font-medium text-gray-700">
              Referral codes
            </span>
            <input
              name="codes"
              placeholder="summer-clinic, alpine-tours"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <span className="mt-1 block text-xs text-gray-500">
              Lower case, digits and hyphens. A code belongs to one partner —
              two partners sharing one would make every attributed customer
              ambiguous.
            </span>
          </label>

          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Contract starts
            </span>
            <input
              name="startsOn"
              type="date"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Contract ends
            </span>
            <input
              name="endsOn"
              type="date"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Notice period (days)
            </span>
            <input
              name="noticeDays"
              type="number"
              min={0}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>

          {refusal && (
            <p className="text-sm text-red-700 sm:col-span-2">{refusal}</p>
          )}

          <div className="flex gap-2 sm:col-span-2">
            <button
              type="submit"
              disabled={add.isPending}
              className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
            >
              {add.isPending ? "Saving…" : "Save partner"}
            </button>
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setRefusal(null);
              }}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {partners.isLoading ? (
          <div className="flex items-center gap-2 p-8 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading partners…
          </div>
        ) : partners.isError ? (
          <div className="p-6 text-sm text-red-700">
            Partners could not be read.
          </div>
        ) : !partners.data?.length ? (
          <div className="p-8 text-sm text-gray-500">
            No partners recorded yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Partner</th>
                  <th className="px-4 py-2 font-medium">Stage</th>
                  <th className="px-4 py-2 font-medium">Referred</th>
                  <th className="px-4 py-2 font-medium">Paying</th>
                  <th className="px-4 py-2 font-medium">Tier</th>
                  <th className="px-4 py-2 font-medium">Contract</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {partners.data.map((partner) => (
                  <tr key={partner.id} className="align-top">
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">
                        {partner.organisation}
                      </span>
                      <span className="mt-0.5 block text-xs text-gray-500">
                        {partner.typeLabel} · {partner.earns}
                      </span>
                      {partner.referralCodes.length > 0 && (
                        <span className="mt-1 block font-mono text-xs text-gray-400">
                          {partner.referralCodes.join(", ")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STAGE_STYLE[partner.stage]}`}
                      >
                        {STAGE_LABEL[partner.stage]}
                      </span>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-gray-900">
                      {partner.attributedCustomers}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-gray-900">
                      {partner.payingCustomers}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-900">
                        {partner.agreedTier}
                      </span>
                      {partner.tierShortfall.below && (
                        <span className="mt-0.5 flex items-center gap-1 text-xs text-amber-700">
                          <AlertTriangle className="h-3 w-3" />
                          earning {partner.earnedTier} — needs{" "}
                          {partner.tierShortfall.needs}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {partner.contract.endsOn
                        ? `to ${fmt.date(partner.contract.endsOn)}`
                        : "—"}
                      {partner.contract.notice.inWindow && (
                        <span className="mt-0.5 block text-xs text-amber-700">
                          notice in {partner.contract.notice.daysLeft} days
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
